'use client';
import Image from 'next/image';
import Link from 'next/link';
import { useMeasure, useMedia } from 'react-use';

import { AdaptiveLogo } from '@/components/adaptive-logo';
import birds2 from '@/components/lottie/files/AT_Sora-Birds_05-2.json';
import { LottiePlay } from '@/components/lottie/lottie-play';

import { CaseStudyWrapper } from '../components/CaseStudyWrapper';
import { ImageData, ImageSlideshow } from '../components/ImageSlideshow';


export const ClientPage = () => {
  const [gridRef, gridMeasure] = useMeasure<HTMLDivElement>();
  const isTablet = useMedia('(min-width: 768px)', false);

  const carouselImages: ImageData[] = [
    {
      src: '/images/sora/slideshow01.png',
      width: 1522,
      height: 966,
      alt: 'Sora Union team collaborating in a creative workspace, showcasing diverse talent.',
    },
    {
      src: '/images/sora/slideshow02.png',
      width: 1556,
      height: 960,
      alt: 'Close-up of Sora Union brand materials and design assets on display.',
    },
    {
      src: '/images/sora/slideshow03.png',
      width: 1312,
      height: 958,
      alt: 'Sora Union members engaged in a remote meeting, highlighting distributed teamwork.',
    },
    {
      src: '/images/sora/slideshow01.png',
      width: 1522,
      height: 966,
      alt: 'Sora Union team collaborating in a creative workspace, showcasing diverse talent.',
    },
    {
      src: '/images/sora/slideshow02.png',
      width: 1556,
      height: 960,
      alt: 'Close-up of Sora Union brand materials and design assets on display.',
    },
    {
      src: '/images/sora/slideshow03.png',
      width: 1312,
      height: 958,
      alt: 'Sora Union members engaged in a remote meeting, highlighting distributed teamwork.',
    },
  ];

  return (
    <CaseStudyWrapper
      mobileBackgroundImage="/images/sora/sora_hero_portrait-min.png"
      backgroundImage="/images/sora/sora_hero-min.png"
      heading="Sora Union"
      headingColor="text-white"
      backgroundColor="bg-[#0A8ABA]"
      nextLink="/airtime"
      hoverTextColor="text-[#0A8ABA]"
    >
      <Link href="/">
        <AdaptiveLogo blendMode={false} aria-label="Go to All Turtles Home" />
      </Link>
      <div className="relative z-20">
        <div className="bg-[#0A8ABA]">
          <div className="static-grid space-y-[32px] pb-16 pt-6 tablet:space-y-[45px] tablet:py-[90px]">
            <div className="col-span-full">
              <p className="hidden justify-center font-breit text-[clamp(16px,calc(16px+0.875vw),30px)] font-kraftig uppercase leading-none tracking-wider text-[#F2DC71] tablet:block">
                Sora Union
              </p>
            </div>

            <div className="mobile-only space-y-[32px]:tablet:space-y-[45px] col-span-full grid grid-cols-12">
              <div className="col-span-full tablet:col-span-9">
                <p className="font-soehne text-[clamp(18px,calc(18px+1.625vw),44px)] font-buch leading-[140%] tracking-[0.21px] text-white">
                  The best ideas arise organically. Sora Union founder Nate Dalton and All Turtles’ Phil Libin
                  experienced this as they walked through the Crystal Bridges Museum of American Art one day, when a
                  free-flowing conversation took an unexpected turn: as talented people around the globe are
                  increasingly displaced by conflict and climate change, can they find stability in the emerging
                  paradigm of distributed work?
                </p>
              </div>

              <div className="col-span-full mobile-only:pt-[45px] tablet:col-span-3 tablet:col-start-11">
                <p className="font-soehne text-[clamp(16px,calc(16px+0.125vw),18px)] font-buch leading-[200%] tracking-[0.21] text-[#F2DC71]">
                  We provided
                </p>
                <ul className="list-none font-soehne text-[clamp(16px,calc(16px+0.125vw),18px)] font-normal uppercase leading-[150%] tracking-[0.21] text-white desktop:mt-0 desktop:leading-[200%]">
                  <li>Brand + Design</li>
                  <li>Intellectual Property</li>
                  <li>Finance</li>
                  <li>Recruiting</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white">
          <div className="static-grid py-16 tablet:py-[200px]">
            <div className="col-span-full flex flex-col tablet:col-span-5">
              <h2 className="liga mb-[48px] font-heldane text-[clamp(38px,calc(38px+3.25vw),90px)] font-buch leading-[110%] tracking-[-2px] text-[#0B91BF]">
                Talent is everywhere, opportunity is not
              </h2>
              <p className="font-soehne text-[clamp(18px,calc(18px+0.375vw),24px)] font-buch leading-[172%] tracking-[0.21] text-navy">
                The answer is a resounding yes. Recognizing that talent is everywhere but opportunity is not, Sora Union
                was founded on a commitment to reshape the future for at-risk knowledge workers by matching them with
                stable work opportunities. It’s a grand ambition, and a perfect collaborative fit with All Turtles’
                mission. 
              </p>
            </div>
            <div className="col-span-full flex items-start justify-start mobile-only:mt-12 tablet:col-span-6 tablet:col-start-7">
              <Image
                className="relative"
                src="/images/sora/Maryna.png"
                alt="Portrait of Maryna, Product and Brand Designer at Sora Union"
                width={745}
                height={745}
                priority
              />
            </div>
            <div className="sub-grid col-span-full items-start">
              <div className="col-span-full mb-12 mt-12 flex flex-col tablet:col-span-8 tablet:col-start-2 tablet:mb-[56px] tablet:mt-[190px]">
                <p className="font-soehne text-[clamp(18px,calc(18px+0.375vw),24px)] font-buch leading-[172%] tracking-[0.21] text-navy">
                  All Turtles has been a close partner to Sora Union since their founding, beginning with brand creation
                  and continuing into engineering support. With our expertise in recruiting for startups, we’ve also
                  helped Sora Union build out their team across design, engineering, and business operations.
                </p>
              </div>
              <div className="col-span-full hidden tablet:col-span-2 tablet:col-start-11 tablet:block">
                <LottiePlay data={birds2} />
              </div>
            </div>
            <div className="sub-grid col-span-full items-start">
              <div className="col-span-full h-full w-full tablet:col-span-6 tablet:col-start-7">
                <Image
                  className="relative z-10 mobile-only:pb-[36px] tablet:top-[72px]"
                  src="/images/sora/sora_colorful_logo.png"
                  alt="Sora Union colorful logo representing brand identity"
                  width={2779}
                  height={861}
                  priority
                />
              </div>
            </div>

            <div className="sub-grid col-span-full items-start" ref={gridRef}>
              <div className="relative col-span-full h-full w-full tablet:col-span-7 tablet:col-start-1">
                <Image
                  // className="relative z-0 tablet:-inset-x-[20%] tablet:-inset-y-[10%] desktop:-inset-y-[15%] desktop4k:-inset-y-[20%]"
                  className={`!h-auto !w-full transition-opacity duration-300 tablet:absolute tablet:right-0 tablet:top-0 ${
                    gridMeasure.width && isTablet !== undefined ? 'opacity-100' : 'opacity-0'
                  }`}
                  style={{
                    transform: isTablet ? `translateX(-${gridMeasure.width / 12}px)` : 'translateX(0)',
                  }}
                  src="/images/sora/sora_concept.png"
                  alt="Concept illustration showing Sora Union's brand colors and design"
                  width={1922}
                  height={2096}
                />
              </div>
              <div className="col-span-full h-full w-full space-y-[48px] pt-[48px] tablet:col-span-5 tablet:col-start-8">
                <p className="font-soehne text-[clamp(18px,calc(18px+0.375vw),24px)] font-buch leading-[172%] tracking-[0.21] text-navy tablet:pt-[72px]">
                  Sora Union’s brand identity had to speak to two core audiences: displaced talent looking for work, and
                  the companies that could benefit from access to their skills and knowledge.
                </p>
                <p className="font-soehne text-[clamp(18px,calc(18px+0.375vw),24px)] font-buch leading-[172%] tracking-[0.21] text-navy">
                  On one hand, Sora Union’s visual identity needed to make people feel inspired, uplifted, and hopeful.
                  On the other, we needed to communicate the values of a hyper-creative, competent, and globally
                  distributed workforce, ready to go.
                </p>
                <p className="font-heldane text-[clamp(28px,calc(28px+2vw),60px)] font-buch leading-[172%] text-[#0B91BF]">
                  The encompassing brand value we wanted to capture for Sora Union was that people are what matter.
                </p>
              </div>
            </div>
          </div>

          <div className="mobile-only:static-grid tablet:sub-grid col-span-full items-start">
            <div className="col-span-full tablet:mx-[72px]">
              {isTablet ? (
                <Image
                  className="relative"
                  src="/images/sora/sora_services.png"
                  alt="Sora Union services overview for desktop and tablet devices"
                  width={5330}
                  height={933}
                />
              ) : (
                <Image
                  className="relative"
                  src="/images/sora/mobile_services.png"
                  alt="Sora Union services overview for mobile devices"
                  width={1140}
                  height={807}
                />
              )}
            </div>
          </div>

          <div className="static-grid">
            <div className="sub-grid col-span-full pb-[51px] pt-[126px] tablet:pb-[132px]">
              <div className="col-span-full flex h-full w-full flex-col justify-center space-y-[32px] tablet:col-span-5 tablet:col-start-1 tablet:space-y-[45px]">
                <p className="font-soehne text-[clamp(18px,calc(18px+0.375vw),24px)] font-buch leading-[172%] tracking-[0.21] text-navy">
                  The word ‘Sora,’ meaning ‘sky’ in Japanese, sounds like ‘soar,’ to fly or rise high in the air. When
                  spoken aloud it feels regal, aspirational, and melodic. It’s an apt metaphor for the mission. War and
                  climate change may force people out of their homelands—but wherever they go, the sky, sunsets, and
                  stars always remain. Times may be hard, but there is always potential for something better.
                </p>
                <p className="font-soehne text-[clamp(18px,calc(18px+0.375vw),24px)] font-buch leading-[172%] tracking-[0.21] text-navy">
                  Instead of being defined by their circumstances, talented workers can be empowered by their own skills
                  and capabilities. Companies seeking professional services can tap into incredible talent that may
                  previously have been unavailable to them. Together, they can soar.
                </p>
              </div>
              <div className="col-span-full w-full mobile-only:mt-12 tablet:col-span-7 tablet:col-start-7">
                <Image
                  className="relative"
                  src="/images/sora/Sani.png"
                  alt="Portrait of Sani, Engineering Manager at Sora Union"
                  width={745}
                  height={745}
                />
              </div>
            </div>
            <div className="sub-grid col-span-full items-start pb-[51px] tablet:pb-[132px]">
              <div className="relative col-span-full h-full w-full mobile-only:order-2 mobile-only:mt-12 tablet:col-span-7 tablet:col-start-1">
                {isTablet ? (
                  <Image
                    className={`!h-auto !w-full transition-opacity duration-300 tablet:absolute tablet:right-0 tablet:top-0 ${
                      gridMeasure.width && isTablet !== undefined ? 'opacity-100' : 'opacity-0'
                    }`}
                    style={{
                      transform: isTablet ? `translateX(-${gridMeasure.width / 12}px)` : 'translateX(0)',
                    }}
                    src="/images/sora/sora_economist.png"
                    alt="Data visualization from The Economist featured in Sora Union case study"
                    width={2838}
                    height={1764}
                  />
                ) : (
                  <Image
                    className={`!h-auto !w-full transition-opacity duration-300 tablet:absolute tablet:right-0 tablet:top-0 ${
                      gridMeasure.width && isTablet !== undefined ? 'opacity-100' : 'opacity-0'
                    }`}
                    style={{
                      transform: isTablet ? `translateX(-${gridMeasure.width / 12}px)` : 'translateX(0)',
                    }}
                    src="/images/sora/economist_mobile.png"
                    alt="Mobile version of The Economist data visualization for Sora Union"
                    width={1524}
                    height={2120}
                  />
                )}
              </div>
              <div className="col-span-full h-full w-full mobile-only:order-1 tablet:col-span-5 tablet:col-start-8">
                <p className="font-heldane text-[clamp(28px,calc(28px+2vw),60px)] font-buch text-[#0B91BF]">
                  “War and climate change may force people out of their homelands—but wherever they go, the sky,
                  sunsets, and stars always remain.”
                </p>
              </div>
            </div>
          </div>

          <ImageSlideshow
            images={carouselImages}
            autoplay={{
              delay: 3000,
              disableOnInteraction: false,
            }}
          />

          <div className="static-grid">
            <div className="col-span-full py-12 text-center tablet:py-[80px]">
              <h4 className="font-heldane text-[clamp(36px,calc(24px+2.25vw),72px)] font-buch leading-[140%] tracking-[-1%] text-navy">
                An ever-changing, dynamic pallette
              </h4>
            </div>
          </div>
        </div>

        <div className="w-full overflow-x-auto bg-white">
          <div className="mx-auto w-full max-w-[1920px]">
            <Image
              className="h-[45vh] w-auto object-cover tablet:h-auto tablet:w-full tablet:object-contain"
              src="/images/sora/sora_dynamic_palette.png"
              alt="Sora Union's dynamic palette of brand colors"
              width={5754}
              height={2517}
            />
          </div>
        </div>

        <div className="bg-white">
          <div className="static-grid items-start pt-[54px]">
            <div className="col-span-full grid grid-cols-2 gap-x-[32px] gap-y-[46px] mobile-only:order-2 mobile-only:mt-12 tablet:col-span-6">
              <div className="col-span-2 self-start overflow-hidden rounded-md bg-slate-400">
                <Image
                  className="relative"
                  src="/images/sora/sora_computer.png"
                  alt="Sora Union computer interface showcasing brand application"
                  width={1808}
                  height={1140}
                />
              </div>
              <div className="col-span-1 self-start overflow-hidden rounded-md bg-slate-400">
                <Image
                  className="relative"
                  src="/images/sora/sora_slide2.png"
                  alt="Sora Union brand slide 2"
                  width={880}
                  height={840}
                />
              </div>
              <div className="col-span-1 self-start overflow-hidden rounded-md bg-slate-400">
                <Image
                  className="relative"
                  src="/images/sora/sora_slide3.png"
                  alt="Sora Union brand slide 3"
                  width={878}
                  height={840}
                />
              </div>
            </div>
            <div className="col-span-full space-y-[48px] mobile-only:order-1 tablet:col-start-8">
              <h2 className="font-heldane text-[clamp(48px,calc(48px+2.625vw),90px)] font-buch leading-[110%] tracking-[-2px] text-[#0B91BF]">
                What color is the sky right now?
              </h2>
              <p className="font-soehne text-[clamp(18px,calc(18px+0.375vw),24px)] font-buch leading-[172%] tracking-[0.21] text-navy">
                Sora Union’s primary icon comprises three birds in flight, representing a flock of migratory birds—a
                strong metaphor for community, teamwork, and moving both forward and upward. 
              </p>
              <p className="font-soehne text-[clamp(18px,calc(18px+0.375vw),24px)] font-buch leading-[172%] tracking-[0.21] text-navy">
                As for the sky itself, the simple solution would have been to make it a neutral blue. But the sky is as
                varied as the cultures represented by Sora’s workforce, ever-changing and dynamic. So we expanded the
                concept of a brand color system to capture all the changing colors of the sky, connected yet
                personalized to a viewer’s time zone through a 24-hour system of complementary palettes and gradients.
              </p>
              <p className="font-soehne text-[clamp(18px,calc(18px+0.375vw),24px)] font-buch leading-[172%] tracking-[0.21] text-navy">
                The final result is a robust identity that exudes the quality, aspiration, and innovation that Sora
                Union’s big idea demands.
              </p>
            </div>
          </div>
        </div>
      </div>
    </CaseStudyWrapper>
  );
};
